/**
 * CustomerRecordingsSection — collapsible "Past recordings" panel for
 * the customer detail page. Pulls every recording for this customer
 * the current user is allowed to see (main-company users see every
 * agent's recording; dealers see only their own — enforced by
 * /api/field-tools/recordings).
 *
 * Auto-hides when the user lacks field-tools permission OR when there
 * are zero recordings yet.
 */
import { useEffect, useState } from "react";
import { Microphone, CaretDown, CaretRight, Clock, Sparkle, Spinner, Warning, CheckCircle, Hourglass, Play } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useFieldTools } from "@/lib/useFieldTools";

const STATUS_LABEL = {
  pending: { label: "Queued", icon: Hourglass, tone: "text-slate-500" },
  transcribing: { label: "Transcribing…", icon: Sparkle, tone: "text-blue-600 animate-pulse" },
  summarizing: { label: "Summarizing…", icon: Sparkle, tone: "text-blue-600 animate-pulse" },
  completed: { label: "Done", icon: CheckCircle, tone: "text-emerald-600" },
  failed_read: { label: "Failed (read)", icon: Warning, tone: "text-rose-600" },
  failed_transcribe: { label: "Failed (transcribe)", icon: Warning, tone: "text-rose-600" },
};

export default function CustomerRecordingsSection({ customerId, customerName, refreshKey = 0 }) {
  const { allowed } = useFieldTools();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [expandedId, setExpandedId] = useState("");

  async function load() {
    if (!customerId) return;
    setLoading(true);
    try {
      const r = await api.get("/field-tools/recordings", { params: { customer_id: customerId } });
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }

  useEffect(() => {
    if (allowed && customerId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, allowed, refreshKey]);

  // After a brand-new upload (refreshKey changed), auto-expand the section
  // so the user can see their recording lighting up the AI pipeline.
  useEffect(() => {
    if (refreshKey > 0) setOpen(true);
  }, [refreshKey]);

  // Poll while any recording is still processing so the UI updates without a refresh.
  useEffect(() => {
    if (!items.some((it) => ["pending", "transcribing", "summarizing"].includes(it.status))) return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  if (!allowed) return null;
  if (!loading && items.length === 0) return null;

  return (
    <section className="bg-white border border-slate-200 rounded-md" data-testid={`customer-recordings-${customerId}`}>
      <button onClick={() => setOpen((v) => !v)}
              data-testid={`customer-recordings-toggle-${customerId}`}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left">
        <Microphone size={14} weight="fill" className="text-rose-600" />
        <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex-1">
          Past recordings <span className="text-slate-400">({items.length})</span>
        </span>
        {open
          ? <CaretDown size={12} weight="bold" className="text-slate-400" />
          : <CaretRight size={12} weight="bold" className="text-slate-400" />}
      </button>

      {open && (
        <div className="border-t border-slate-100">
          {loading && items.length === 0 ? (
            <div className="py-6 text-center"><Spinner size={18} className="animate-spin text-slate-400" /></div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((it) => {
                const st = STATUS_LABEL[it.status] || STATUS_LABEL.pending;
                const isExp = expandedId === it.id;
                const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
                const audioUrl = `${apiBase}/api/field-tools/recordings/${it.id}/audio`;
                return (
                  <li key={it.id} className="px-3 py-3"
                      data-testid={`customer-recording-${it.id}`}>
                    <button onClick={() => setExpandedId(isExp ? "" : it.id)}
                            data-testid={`customer-recording-expand-${it.id}`}
                            className="w-full flex items-center gap-2 text-left">
                      <Clock size={12} weight="fill" className="text-slate-400 flex-shrink-0" />
                      <span className="text-[12px] text-slate-600 font-mono flex-shrink-0">
                        {fmtDate(it.created_at)}
                      </span>
                      <span className="text-[12px] text-slate-700 flex-1 truncate">
                        {it.agent_email}
                      </span>
                      <span className="text-[10px] text-slate-500 flex-shrink-0">{fmtDur(it.duration_s)}</span>
                      <span className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-0.5 flex-shrink-0 ${st.tone}`}>
                        <st.icon size={11} weight="fill" /> {st.label}
                      </span>
                    </button>

                    {isExp && (
                      <div className="mt-2 pl-5 space-y-2">
                        {/* Audio player */}
                        <div className="flex items-center gap-2">
                          <Play size={11} weight="fill" className="text-slate-400" />
                          <audio controls preload="none" src={audioUrl}
                                 data-testid={`customer-recording-audio-${it.id}`}
                                 className="w-full max-w-md h-8" />
                        </div>

                        {/* Summary */}
                        {it.summary && (
                          <div>
                            <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">Summary</div>
                            <p className="text-[12px] text-slate-800 bg-blue-50 border-l-4 border-blue-600 px-2 py-1">
                              {it.summary}
                            </p>
                          </div>
                        )}

                        {/* Next actions */}
                        {Array.isArray(it.next_actions) && it.next_actions.length > 0 && (
                          <div>
                            <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">Suggested next actions</div>
                            <ul className="text-[12px] text-slate-800 list-disc list-inside space-y-0.5">
                              {it.next_actions.map((a, i) => (<li key={i}>{a}</li>))}
                            </ul>
                          </div>
                        )}

                        {/* Topics */}
                        {Array.isArray(it.topics) && it.topics.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {it.topics.map((t) => (
                              <span key={t} className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 rounded">
                                {t}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Transcript */}
                        {it.transcript && (
                          <details>
                            <summary className="cursor-pointer text-[10px] font-bold uppercase tracking-wider text-slate-500">
                              Full transcript
                            </summary>
                            <p className="mt-1 text-[12px] text-slate-700 bg-slate-50 border border-slate-200 p-2 whitespace-pre-wrap">
                              {it.transcript}
                            </p>
                          </details>
                        )}

                        {/* Empty state for still-processing rows */}
                        {!it.transcript && ["pending", "transcribing", "summarizing"].includes(it.status) && (
                          <p className="text-[11px] text-slate-400 italic">
                            AI is still working on this one — it will update shortly.
                          </p>
                        )}

                        {it.status?.startsWith("failed") && (
                          <p className="text-[11px] text-rose-600">
                            <Warning size={11} weight="fill" className="inline mr-1" />
                            Could not process this recording — try recording again or contact support.
                          </p>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

function fmtDur(s) {
  s = Math.round(Number(s) || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}
